// 1. External libraries (React first)
import React, { useState, useMemo } from 'react'
import { ButtonGroup, Stack } from 'react-bootstrap'

// 2. Type imports
import type { InventoryBySerialNumber, Product } from '@ztechapps/common.contract'
import type { SerialNumberStrategy } from './strategies'

// 3. Internal imports
import { SerialNumberTypeEnum } from '@ztechapps/common.contract'
import { BusyButton } from '@ztechapps/webui.button'

// 4. Local imports
import { BatchStrategy, ManualStrategy, PatternStrategy } from './strategies'

export type EntryMode = 'manual' | 'pattern' | 'batch'

export type InventoryBySerialNumberCodeInputProps = {
  disabled?: boolean
  value: InventoryBySerialNumber[]
  onChange: (value: InventoryBySerialNumber[]) => void
  maxQtySerialNumber?: number
  product?: Product
}

export function InventoryBySerialNumberCodeInput(props?: InventoryBySerialNumberCodeInputProps) {
  const { value, onChange, maxQtySerialNumber = 1, disabled, product } = props ?? {}
  const isBatchSerial = product?.serialNumberType === SerialNumberTypeEnum.Batch

  // Determine the mode based on product type
  const initialMode: EntryMode = isBatchSerial ? 'batch' : 'manual'
  const [mode, setMode] = useState<EntryMode>(initialMode)
  const [error, setError] = useState<string | null>(null)

  // Strategy map
  const strategies: Record<EntryMode, SerialNumberStrategy> = useMemo(
    () => ({
      batch: BatchStrategy,
      manual: ManualStrategy,
      pattern: PatternStrategy,
    }),
    []
  )

  const currentStrategy = strategies[mode]

  // Initialize input value based on current strategy
  const [inputValue, setInputValue] = useState(() => currentStrategy.getInitialValue(value || []))

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue)
    setError(null)

    const result = currentStrategy.validator(newValue, maxQtySerialNumber)

    if (result.isValid) {
      onChange(result.serialNumbers as InventoryBySerialNumber[])
    } else {
      setError(result.error || null)
    }
  }

  const handleModeChange = (newMode: EntryMode) => {
    if (newMode === mode) return

    setMode(newMode)
    setError(null)

    // Reset input value when switching modes
    const newStrategy = strategies[newMode]
    const newInputValue = newStrategy.getInitialValue(value || [])
    setInputValue(newInputValue)

    // Clear serial numbers when switching modes
    onChange([])
  }

  return (
    <Stack gap={3}>
      {/* Mode Toggle - Only show for individual serial numbers */}
      {!isBatchSerial && (
        <div>
          <ButtonGroup>
            <BusyButton
              variant={mode === 'manual' ? 'primary' : 'outline-secondary'}
              onClick={() => handleModeChange('manual')}
              bsSize="sm"
              icon={'keyboard'}
              text="Manual Entry"
              disabled={disabled}
            />
            <BusyButton
              variant={mode === 'pattern' ? 'primary' : 'outline-secondary'}
              onClick={() => handleModeChange('pattern')}
              bsSize="sm"
              icon={'wand-magic-sparkles'}
              text="Pattern Generator"
              disabled={disabled}
            />
          </ButtonGroup>
        </div>
      )}

      {/* Render the current strategy's input */}
      <currentStrategy.InputComponent
        value={inputValue}
        onChange={handleInputChange}
        disabled={disabled}
        maxQuantity={maxQtySerialNumber}
        error={error || undefined}
      />
    </Stack>
  )
}
